// A colour option drawn as Shopify themes draw it: a round swatch per value, filled with the colour its name
// says. A value names its colour in English, French or Arabic ("Black", "Noir", "أسود"), or carries a hex code
// of its own ("Sable #d8c3a5"). An option is drawn as swatches only when every value has a colour, so a
// shopper never meets a guessed one.

/** The option names that hold colours. */
const COLOUR_OPTION_RE = /^(colou?rs?|couleurs?|coloris|teintes?|لون|اللون|الألوان)$/i;

/** The colour each common name stands for, in the languages a store is served in. */
const NAMED: Readonly<Record<string, string>> = {
  black: '#111111', noir: '#111111', 'أسود': '#111111', white: '#ffffff', blanc: '#ffffff', 'أبيض': '#ffffff',
  red: '#d0201a', rouge: '#d0201a', 'أحمر': '#d0201a', brown: '#7b4a2d', marron: '#7b4a2d', 'بني': '#7b4a2d',
  beige: '#e9dcc6', 'بيج': '#e9dcc6', camel: '#c19a6b', cognac: '#9a463d', taupe: '#8b7d6b', nude: '#e3bc9a', tan: '#d2b48c',
  cream: '#fff8e1', creme: '#fff8e1', ivory: '#fffff0', ivoire: '#fffff0', grey: '#9e9e9e', gray: '#9e9e9e', gris: '#9e9e9e', 'رمادي': '#9e9e9e',
  blue: '#1e4fd8', bleu: '#1e4fd8', 'أزرق': '#1e4fd8', navy: '#1b2a49', marine: '#1b2a49', green: '#2e7d32', vert: '#2e7d32', 'أخضر': '#2e7d32',
  khaki: '#b5a46a', kaki: '#b5a46a', olive: '#6b7a2c', pink: '#f4a6c1', rose: '#f4a6c1', 'وردي': '#f4a6c1', burgundy: '#800020', bordeaux: '#800020',
  gold: '#d4af37', dore: '#d4af37', 'ذهبي': '#d4af37', silver: '#c0c0c0', argent: '#c0c0c0', 'فضي': '#c0c0c0',
  yellow: '#f2c200', jaune: '#f2c200', orange: '#f57c00', purple: '#6a1b9a', violet: '#6a1b9a', mauve: '#b784a7', turquoise: '#1fb5ac',
  // The colours Moroccan crafts are sold in, so a terracotta tajine or a saffron cushion gets its swatch too.
  terracotta: '#c0603c', safran: '#f4b400', saffron: '#f4b400', indigo: '#3f3d8f', majorelle: '#5b5fc7', ecru: '#efe6d2', naturel: '#e8dcc8',
  natural: '#e8dcc8', menthe: '#98d7c2', mint: '#98d7c2', sable: '#d8c3a5', sand: '#d8c3a5', moutarde: '#d4a017', mustard: '#d4a017', corail: '#ff7f50', coral: '#ff7f50'
};

/** A hex colour code a value may carry, three or six digits. */
const HEX_RE = /#([0-9a-f]{3}|[0-9a-f]{6})\b/i;

// Reports whether an option holds colours, by its name.
export const isColourOption = (name: string): boolean => COLOUR_OPTION_RE.test(name.trim());

// Reads the colour a value stands for: its own hex code, its name, or the last word of its name; null when none.
export function swatchColour(value: string): string | null {
  const hex = HEX_RE.exec(value)?.[0];
  if (hex) return hex.toLowerCase();
  const plain = value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').normalize('NFC').trim().toLowerCase();
  return NAMED[plain] ?? NAMED[plain.split(/\s+/).pop() ?? ''] ?? null;
}

// Gives every value's colour when all of them have one, else null, so the option is drawn as words instead.
export function swatchColours(values: readonly string[]): ReadonlyMap<string, string> | null {
  const colours = values.map((value) => [value, swatchColour(value)] as const);
  return colours.every(([, colour]) => colour !== null) ? new Map(colours as [string, string][]) : null;
}

// Gives a value's name without the hex code it may carry, as the shopper reads it.
export const swatchName = (value: string): string => value.replace(HEX_RE, '').trim() || value;
